import { PAYMENT_TARIFF } from '../../types';

export const VALID_TARIFFS = new Set<string>(Object.values(PAYMENT_TARIFF));

/** Tariff used app-wide when the URL doesn't carry a (valid) `?tariff`. */
export const DEFAULT_TARIFF = PAYMENT_TARIFF.V41;

export const parseTariff = (value: string | null): PAYMENT_TARIFF => {
  if (!value) return DEFAULT_TARIFF;
  return VALID_TARIFFS.has(value) ? (value as PAYMENT_TARIFF) : DEFAULT_TARIFF;
};

// Normalizes unknown tariff to the default; preserves null when tariff is absent
export const sanitizeTariff = (value: string | null): string | null =>
  value && !VALID_TARIFFS.has(value) ? DEFAULT_TARIFF : value;

/**
 * Regular → discount (special-offer) tariff pairs. The discount tariff is applied
 * when the user declines the regular offer (closes the payment modal / sees the
 * discount modal). Single source of truth — payment modals must not hardcode pairs.
 */
export const TARIFF_TO_DISCOUNT: Readonly<Partial<Record<PAYMENT_TARIFF, PAYMENT_TARIFF>>> = {
  [PAYMENT_TARIFF.V6]: PAYMENT_TARIFF.V7,
  [PAYMENT_TARIFF.V8]: PAYMENT_TARIFF.V9,
  [PAYMENT_TARIFF.V10]: PAYMENT_TARIFF.V11,
  [PAYMENT_TARIFF.V12]: PAYMENT_TARIFF.V13,
  [PAYMENT_TARIFF.V14]: PAYMENT_TARIFF.V15,
  [PAYMENT_TARIFF.V17]: PAYMENT_TARIFF.V18,
  [PAYMENT_TARIFF.V19]: PAYMENT_TARIFF.V20,
  [PAYMENT_TARIFF.V21]: PAYMENT_TARIFF.V22,
  [PAYMENT_TARIFF.V23]: PAYMENT_TARIFF.V24,
  [PAYMENT_TARIFF.V25]: PAYMENT_TARIFF.V26,
  [PAYMENT_TARIFF.V27]: PAYMENT_TARIFF.V28,
  [PAYMENT_TARIFF.V29]: PAYMENT_TARIFF.V30,
  [PAYMENT_TARIFF.V31]: PAYMENT_TARIFF.V32,
  [PAYMENT_TARIFF.V33]: PAYMENT_TARIFF.V34,
  [PAYMENT_TARIFF.V35]: PAYMENT_TARIFF.V36,
  [PAYMENT_TARIFF.V37]: PAYMENT_TARIFF.V38,
  [PAYMENT_TARIFF.V39]: PAYMENT_TARIFF.V40,
  [PAYMENT_TARIFF.V41]: PAYMENT_TARIFF.V42,
  [PAYMENT_TARIFF.V43]: PAYMENT_TARIFF.V44,
  [PAYMENT_TARIFF.V45]: PAYMENT_TARIFF.V46,
};

/** Discount tariff offered when the current tariff has no dedicated pair. */
export const FALLBACK_DISCOUNT_TARIFF = PAYMENT_TARIFF.V5;

/** Resolves the discount tariff the special offer should switch to. */
export const getDiscountTariff = (tariff: string | null | undefined): PAYMENT_TARIFF =>
  (tariff && TARIFF_TO_DISCOUNT[tariff as PAYMENT_TARIFF]) || FALLBACK_DISCOUNT_TARIFF;

// Discount/special-offer tariffs — drive `state.data.isDiscount` (special-offer
// screen in the V5 paywall).
// NOTE: intentionally narrower than the TARIFF_TO_DISCOUNT pair targets: switching
// to v7/v9/…/v24/v26/v34 does NOT flip isDiscount today. Kept as-is to preserve
// live behaviour; align with TARIFF_TO_DISCOUNT only after product sign-off.
export const DISCOUNT_TARIFFS: ReadonlySet<string> = new Set<string>([
  PAYMENT_TARIFF.V22,
  PAYMENT_TARIFF.V28,
  PAYMENT_TARIFF.V30,
  PAYMENT_TARIFF.V32,
  PAYMENT_TARIFF.V36,
  PAYMENT_TARIFF.V38,
  PAYMENT_TARIFF.V40,
  PAYMENT_TARIFF.V42,
  PAYMENT_TARIFF.V44,
  PAYMENT_TARIFF.V46,
]);

export const isDiscountTariff = (tariff: string | null | undefined): boolean =>
  Boolean(tariff) && DISCOUNT_TARIFFS.has(tariff as string);
